import { useTranslation } from 'react-i18next';
import { trackEvent } from '@shared/analytics';
import { ChevronRightIcon, GiftIcon } from '@shared/ui';
import { giftOffer, useSocialRewardController } from '../model/rewardContext';

/**
 * Подарок над жетонами в боковой панели (ТЗ главной § 11; с главной убран). Развёрнутая панель и
 * выдвижное меню — строка с подписью, свёрнутая — квадрат со значком (подпись — в `aria-label`).
 * Сохранённое пополнение — зелёная точка вместо стрелки. `onOpen` — до открытия диалога: выдвижное
 * меню сначала закрывается.
 */
export function SocialRewardSidebarRow({ collapsed, onOpen }: { collapsed: boolean; onOpen?: () => void }) {
  const { t } = useTranslation();
  const reward = useSocialRewardController();
  const offer = reward ? giftOffer(reward) : null;
  if (!reward || !offer) return null;

  const label = t(offer === 'ready' ? 'socialReward.sidebar.ready' : 'socialReward.sidebar.locked');
  const open = () => {
    onOpen?.();
    trackEvent('social_reward_opened', { place: 'sidebar' });
    reward.show();
  };

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={open}
        aria-label={label}
        aria-haspopup="dialog"
        className="relative mx-auto mb-2 grid h-12 w-12 place-items-center rounded-[14px] border border-solid border-accent/35 bg-accent/[0.06] text-accent-hi transition-colors hover:bg-accent/[0.12]"
      >
        <GiftIcon className="h-5 w-5" aria-hidden />
        {offer === 'ready' && <span aria-hidden className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-success" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={open}
      aria-haspopup="dialog"
      className="mb-2 flex h-11 w-full items-center gap-3 rounded-[14px] border border-solid border-accent/35 bg-accent/[0.06] px-3 text-left text-[14px] font-medium text-fg transition-colors hover:bg-accent/[0.12]"
    >
      <GiftIcon className="h-[18px] w-[18px] shrink-0 text-accent-hi" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {offer === 'ready' ? (
        <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-success" />
      ) : (
        <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden />
      )}
    </button>
  );
}
